'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useLocale } from '@/app/[lang]/providers/LocaleProvider';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useNavigationMenu, type NavigationNode } from '@/hooks/useNavigationMenu';
import type { MenuId } from '@/lib/enum';
import { getMenuSlugs } from '@/lib/getMenuIdFromSlug';

type SidebarNavProps = {
  /**
   * The parent navigation node ID or slug to filter navigation items
   * Pass this to show only children of a specific menu section
   */
  parentId?: number;
  /**
   * Optional parent slug to filter by (alternative to parentId)
   */
  parentSlug?: string;
  /**
   * Optional menu identifier (alias for parentSlug) to keep backward compatibility
   */
  menuId?: MenuId;
  /**
   * Optional title for the navigation section
   */
  title?: string;
  /**
   * Optional class name for styling
   */
  className?: string;
};

export default function SidebarNav({ 
  parentId, 
  parentSlug,
  menuId,
  title,
  className = '' 
}: SidebarNavProps) {
  const locale = useLocale();
  const pathname = usePathname();
  const { data: navigationData, isLoading } = useNavigationMenu();
  
  // Remove locale prefix from pathname for comparison with navigation paths
  const pathnameWithoutLocale = useMemo(() => {
    const pathSegments = pathname.split('/').filter(Boolean);
    // Remove the first segment if it's a locale (e.g., 'en' or 'id')
    if (pathSegments.length > 0 && pathSegments[0].length === 2) {
      return '/' + pathSegments.slice(1).join('/');
    }
    return pathname;
  }, [pathname]);
  
  // Debug: Log the current locale and navigation data
  console.log('SidebarNav - Current locale:', locale);
  console.log('SidebarNav - pathname:', pathname);
  console.log('SidebarNav - pathnameWithoutLocale:', pathnameWithoutLocale);
  console.log('SidebarNav - Navigation data:', navigationData);
  
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<number>>(() => {
    // Initialize with sections that have active items
    const initialExpanded = new Set<number>();
    
    if (!navigationData) return initialExpanded;
    
    const checkActiveRecursive = (nodes: NavigationNode[]): void => {
      nodes.forEach((node) => {
        const hasActivePath = pathnameWithoutLocale === node.path || pathnameWithoutLocale.startsWith(node.path + '/');
        if (hasActivePath && node.children.length > 0) {
          initialExpanded.add(node.id);
        }
        if (node.children.length > 0) {
          checkActiveRecursive(node.children);
        }
      });
    };
    
    checkActiveRecursive(navigationData);
    return initialExpanded;
  });
  
  const toggleSection = (nodeId: number) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const slugContext = useMemo(() => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const menuSlugFromPath = pathSegments.length > 1 ? pathSegments[1] : null;
    const candidates = new Set<string>();

    if (menuSlugFromPath) {
      candidates.add(menuSlugFromPath);
    }

    if (parentSlug) {
      candidates.add(parentSlug);
    }

    if (menuId) {
      getMenuSlugs(menuId).forEach((slug) => candidates.add(slug));
    }

    return {
      pathSegments,
      menuSlugFromPath,
      slugCandidates: Array.from(candidates),
    };
  }, [pathname, parentSlug, menuId]);

  // Find the relevant navigation items based on parentId or parentSlug
  const filteredNodes = useMemo(() => {
    if (!navigationData) return [];
    const { pathSegments, menuSlugFromPath, slugCandidates } = slugContext;
    
    console.log('SidebarNav - pathname:', pathname);
    console.log('SidebarNav - pathSegments:', pathSegments);
    console.log('SidebarNav - menuSlugFromPath:', menuSlugFromPath);
    console.log('SidebarNav - Filter params:', { parentId, parentSlug, menuId, slugCandidates });
    console.log('SidebarNav - Available root slugs:', navigationData.map(n => ({ id: n.id, slug: n.slug, title: n.title })));
    
    // If parentId or any slug candidate is provided, find that node and return its children
    if (parentId || slugCandidates.length > 0) {
      const findNode = (nodes: NavigationNode[]): NavigationNode | null => {
        for (const node of nodes) {
          console.log('SidebarNav - Checking node:', { id: node.id, slug: node.slug, title: node.title, lookingFor: slugCandidates, parentId });
          if (parentId && node.id === parentId) {
            console.log('SidebarNav - Match by parentId!');
            return node;
          }
          if (slugCandidates.includes(node.slug)) {
            console.log('SidebarNav - Match by slug!');
            return node;
          }
          if (node.children.length > 0) {
            const found = findNode(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      
      const parentNode = findNode(navigationData);
      console.log('SidebarNav - Found parent node:', parentNode);
      if (parentNode) {
        console.log('SidebarNav - Parent node children:', parentNode.children.map(c => ({ id: c.id, slug: c.slug, title: c.title })));
      }
      return parentNode ? parentNode.children : [];
    }
    
    // Otherwise return all root level nodes
    console.log('SidebarNav - Returning all root nodes:', navigationData.length);
    return navigationData;
  }, [navigationData, parentId, pathname, slugContext]);

  // Get the title from the parent node if not provided
  const sectionTitle = useMemo(() => {
    if (title) return title;

    const { slugCandidates } = slugContext;

    if (!navigationData || (!parentId && slugCandidates.length === 0)) return '';
    
    const findNode = (nodes: NavigationNode[]): NavigationNode | null => {
      for (const node of nodes) {
        if (parentId && node.id === parentId) return node;
        if (slugCandidates.includes(node.slug)) return node;
        if (node.children.length > 0) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const parentNode = findNode(navigationData);
    return parentNode?.title || '';
  }, [navigationData, parentId, title, slugContext]);

  // Helper to check if URL matches current path
  const isActive = (path: string) => {
    return pathnameWithoutLocale === path || pathnameWithoutLocale.startsWith(path + '/');
  };

  // Helper to check if node or any of its children are active
  const isNodeActive = (node: NavigationNode): boolean => {
    if (isActive(node.path)) return true;
    return node.children.some(child => isNodeActive(child));
  };

  if (isLoading) {
    return (
      <nav className={className}>
        <div className="px-6 py-4 border-b border-gray-200 bg-primary-50">
          <div className="h-6 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="py-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-6 py-3">
              <div className="h-4 bg-gray-200 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </nav>
    );
  }

  if (!filteredNodes.length) {
    console.log('SidebarNav - No filtered nodes, returning null');
    console.log('SidebarNav - menuId:', menuId, 'parentSlug:', parentSlug, 'parentId:', parentId);
    return null;
  }

  return (
    <nav className={` ${className}`}>
      {/* Menu Title */}
      {sectionTitle && (
        <div className="px-6 py-4 border-b border-gray-200 bg-primary-50">
          <h2 className="text-lg font-bold text-primary-700 text-left">
            {sectionTitle}
          </h2>
        </div>
      )}

      {/* Navigation Items */}
      <div className="py-2">
        {filteredNodes.map((node) => {
          const hasChildren = node.children.length > 0;
          const nodeActive = isNodeActive(node);

          return (
            <div key={node.id} className="mb-1">
              {/* Node Level */}
              {hasChildren ? (
                // Node with children - render as collapsible group
                <div>
                  <button
                    onClick={() => toggleSection(node.id)}
                    className={`w-full flex items-start justify-start px-6 py-3 text-[18px] text-left transition-colors ${
                      nodeActive
                        ? 'bg-primary-50 text-primary-700 font-bold'
                        : 'text-gray-600 hover:bg-gray-50 font-medium'
                    }`}
                  >
                    {expandedSections.has(node.id) ? (
                      <ChevronDown 
                        className={`w-4 h-4 mr-2 transition-transform ${
                          nodeActive ? 'text-primary-700' : 'text-gray-400'
                        }`}
                      />
                    ) : (
                      <ChevronRight 
                        className={`w-4 h-4 mr-2 transition-transform ${
                          nodeActive ? 'text-primary-700' : 'text-gray-400'
                        }`}
                      />
                    )}
                    <span className="flex-1 text-left whitespace-normal wrap-break-word">{node.title}</span>
                  </button>
                  {/* Items under node - collapsible */}
                  {expandedSections.has(node.id) && (
                    <div className="ml-4">
                      {node.children.map((child) => {
                        const childActive = isActive(child.path);
                        return (
                          <Link
                            key={child.id}
                            href={`/${locale}${child.path}`}
                            prefetch={false}
                            className={`flex items-start justify-start px-6 py-2.5 text-[18px] text-left transition-colors group ${
                              childActive
                                ? "bg-primary-50 bg-[url('/images/mesh-119.png')] bg-cover bg-center text-primary-700 font-bold"
                                : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                            }`}
                          >
                            <ChevronRight 
                              className={`w-4 h-4 mr-2 transition-transform ${
                                childActive ? 'text-primary-700' : 'text-gray-400 group-hover:text-primary-600'
                              }`}
                            />
                            <span className="flex-1 text-left whitespace-normal wrap-break-word">{child.title}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                // Node without children - render as direct link
                <Link
                  href={`/${locale}${node.path}`}
                  prefetch={false}
                  className={`flex items-start justify-start px-6 py-3 text-[18px] text-left transition-colors ${
                    nodeActive
                      ? 'bg-primary-100 text-primary-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600 font-medium'
                  }`}
                >
                  <ChevronRight 
                    className={`w-4 h-4 mr-2 transition-transform ${
                      nodeActive ? 'text-primary-700' : 'text-gray-400'
                    }`}
                  />
                  <span className="flex-1 text-left whitespace-normal wrap-break-word">{node.title}</span>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
